/**
 * VistoeraTASK-00637 — Tenant detail 「功能授权」 panel for one workspace_id.
 */
import { Alert, Button, Modal, Space, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  platformGetAuthWorkspaceMatrix,
  platformPutAuthWorkspaceOverride,
} from '../../api'
import type { AuthMatrixRow, AuthWorkspaceMatrixResponse } from '../../types'
import { SectionCard, SummaryCard } from '../adminShared'
import { platformColors } from '../theme'
import WorkspaceAuthMatrixTable from './WorkspaceAuthMatrixTable'
import {
  countWorkspaceOverrides,
  draftAllInherit,
  isWorkspaceDirty,
  mapsFromWorkspaceRows,
  workspaceDirtyEntries,
  type WorkspaceDecision,
} from './workspaceAuthMatrixDraft'

type Props = {
  workspaceId: string
  workspaceName: string
  planKey?: string | null
  planDisplayName?: string | null
}

export default function WorkspaceAuthMatrixPanel({
  workspaceId,
  workspaceName,
  planKey,
  planDisplayName,
}: Props) {
  const [matrix, setMatrix] = useState<AuthWorkspaceMatrixResponse | null>(null)
  const [rows, setRows] = useState<AuthMatrixRow[]>([])
  const [baseline, setBaseline] = useState<Record<string, WorkspaceDecision>>({})
  const [draft, setDraft] = useState<Record<string, WorkspaceDecision>>({})
  const [nodeIds, setNodeIds] = useState<string[]>([])
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadSeq = useRef(0)
  const draftRef = useRef(draft)
  const baselineRef = useRef(baseline)
  draftRef.current = draft
  baselineRef.current = baseline

  const dirty = useMemo(() => isWorkspaceDirty(baseline, draft), [baseline, draft])
  const overrideStats = useMemo(
    () => countWorkspaceOverrides(draft, nodeIds, channelIds),
    [draft, nodeIds, channelIds],
  )

  const applyMatrix = useCallback((data: AuthWorkspaceMatrixResponse, preserveSelection?: boolean) => {
    setMatrix(data)
    const maps = mapsFromWorkspaceRows(data.rows || [])
    setRows(data.rows || [])
    setBaseline(maps.baseline)
    setDraft(maps.draft)
    setNodeIds(maps.nodeIds)
    setChannelIds(maps.channelIds)
    setSelectedNodeId((prev) => {
      if (preserveSelection && prev && maps.nodeIds.includes(prev)) return prev
      return null
    })
  }, [])

  const load = useCallback(() => {
    if (!workspaceId) return
    const seq = ++loadSeq.current
    setLoading(true)
    setError(null)
    platformGetAuthWorkspaceMatrix(workspaceId)
      .then((r) => {
        if (seq !== loadSeq.current) return
        // Defense: never apply another workspace's payload.
        if (r.data.workspace_id && r.data.workspace_id !== workspaceId) {
          setError('授权矩阵 workspace_id 与当前租户不匹配')
          setMatrix(null)
          return
        }
        applyMatrix(r.data)
      })
      .catch((err) => {
        if (seq !== loadSeq.current) return
        setMatrix(null)
        setError((err as Error).message || '加载租户授权矩阵失败')
      })
      .finally(() => {
        if (seq !== loadSeq.current) return
        setLoading(false)
      })
  }, [applyMatrix, workspaceId])

  useEffect(() => {
    load()
  }, [load])

  // Warn on browser leave when dirty.
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const onDecisionChange = (kind: 'node' | 'channel', subjectId: string, decision: WorkspaceDecision) => {
    const key = `${kind}:${subjectId}` as const
    setDraft((prev) => ({ ...prev, [key]: decision }))
  }

  const save = useCallback(async (): Promise<boolean> => {
    const changes = workspaceDirtyEntries(baselineRef.current, draftRef.current)
    if (changes.length === 0) {
      message.info('没有需要保存的修改')
      return true
    }
    setSaving(true)
    const failures: string[] = []
    try {
      for (const change of changes) {
        try {
          await platformPutAuthWorkspaceOverride(workspaceId, {
            subject_kind: change.kind,
            subject_id: change.subjectId,
            decision: change.decision,
          })
        } catch (err) {
          failures.push(`${change.kind}:${change.subjectId} — ${(err as Error).message}`)
        }
      }
      const verified = await platformGetAuthWorkspaceMatrix(workspaceId)
      if (verified.data.workspace_id && verified.data.workspace_id !== workspaceId) {
        message.error('保存校验失败：workspace_id 不匹配')
        return false
      }
      applyMatrix(verified.data, true)
      if (failures.length > 0) {
        message.error(`部分保存失败（${failures.length}/${changes.length}）：${failures[0]}`)
        return false
      }
      message.success(`已保存 ${changes.length} 项租户覆盖授权`)
      return true
    } catch (err) {
      message.error(`保存失败：${(err as Error).message}`)
      return false
    } finally {
      setSaving(false)
    }
  }, [applyMatrix, workspaceId])

  const requestResetToPlan = () => {
    Modal.confirm({
      title: '重置为套餐默认？',
      content:
        '将把当前 Workspace 的节点及通道覆盖授权全部恢复为「继承」套餐默认。不会修改套餐本身、其他 Workspace、成员独立授权或旧版 Entitlements。',
      okText: '确认重置',
      cancelText: '取消',
      onOk: async () => {
        const next = draftAllInherit(nodeIds, channelIds)
        setDraft(next)
        // Persist immediately so "reset" is not only a local draft.
        const prevBaseline = baselineRef.current
        draftRef.current = next
        const changes = workspaceDirtyEntries(prevBaseline, next)
        if (changes.length === 0) {
          message.info('当前已全部继承套餐默认')
          return
        }
        setSaving(true)
        const failures: string[] = []
        try {
          for (const change of changes) {
            try {
              await platformPutAuthWorkspaceOverride(workspaceId, {
                subject_kind: change.kind,
                subject_id: change.subjectId,
                decision: 'INHERIT',
              })
            } catch (err) {
              failures.push(`${change.kind}:${change.subjectId}`)
            }
          }
          const verified = await platformGetAuthWorkspaceMatrix(workspaceId)
          applyMatrix(verified.data, true)
          if (failures.length) {
            message.error(`重置部分失败（${failures.length}）`)
            return
          }
          message.success(`已重置 ${changes.length} 项为继承套餐`)
        } catch (err) {
          message.error(`重置失败：${(err as Error).message}`)
        } finally {
          setSaving(false)
        }
      },
    })
  }

  const planLabel = planDisplayName || planKey || '—'

  return (
    <div>
      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={error}
          action={
            <Button size="small" onClick={load}>
              重试
            </Button>
          }
        />
      ) : null}

      <Space wrap style={{ marginBottom: 12 }}>
        <SummaryCard title="当前租户" value={workspaceName} iconBg={platformColors.primarySoft} icon="🏢" />
        <SummaryCard title="当前套餐" value={planLabel} iconBg={platformColors.infoBg} icon="📦" loading={loading} />
        <SummaryCard
          title="授权模式"
          value={matrix?.enforcement_mode || '—'}
          hint="Preview 保持 compat"
          iconBg={platformColors.warningBg}
          icon="⚙"
          loading={loading}
        />
        <SummaryCard
          title="已覆盖节点"
          value={overrideStats.nodes}
          iconBg={platformColors.successBg}
          icon="☑"
          loading={loading}
        />
        <SummaryCard
          title="已覆盖通道"
          value={overrideStats.channels}
          iconBg={platformColors.infoBg}
          icon="⇄"
          loading={loading}
        />
      </Space>

      <SectionCard
        title={
          <Space>
            <span>租户功能授权矩阵</span>
            {dirty ? <Tag color="orange">未保存</Tag> : <Tag>已同步</Tag>}
          </Space>
        }
        extra={
          <Space wrap>
            <Button disabled={saving || loading} onClick={requestResetToPlan}>
              重置为套餐默认
            </Button>
            <Button disabled={!dirty || saving} onClick={load}>
              放弃修改
            </Button>
            <Button type="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
              保存更改
            </Button>
          </Space>
        }
        allowOverflow
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          为当前租户配置可使用的功能节点及其 Provider 通道。默认继承所属套餐；管理员可对单个节点或通道设置覆盖授权（继承 /
          允许 / 禁止）。全站已关闭的项无法被 Workspace 允许绕过。最终有效状态来自后端 Resolver。
        </Typography.Paragraph>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          workspace_id：{workspaceId}
          {planKey ? ` · plan_key：${planKey}` : ''}
        </Typography.Text>
        <WorkspaceAuthMatrixTable
          rows={rows}
          draft={draft}
          baseline={baseline}
          selectedNodeId={selectedNodeId}
          onSelectNode={setSelectedNodeId}
          onDecisionChange={onDecisionChange}
          loading={loading}
        />
      </SectionCard>
    </div>
  )
}
